import React, { useEffect, useState } from 'react';
import { Package2, ShoppingBag, Store, AlertCircle, Check, ExternalLink, Zap, Webhook, HelpCircle, Loader2, CheckCircle2, ChevronDown, ChevronUp, ArrowRight, Info, Code, RefreshCw } from 'lucide-react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { Switch } from '@headlessui/react';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import type { Database } from '../../types/supabase';
import { toast } from 'react-hot-toast';
import { updateChatbotConfigSection, getChatbotConfigSection } from '../../utils/chatbotConfigAccess';

type IntegrationType = 'shopify' | 'prestashop' | 'webhook' | null;
type WebhookProvider = 'zapier' | 'make' | 'custom';

interface OrderTrackingSettings {
  enabled: boolean;
  integration_type: IntegrationType;
  tracking_url: string;
  webhook_config: {
    url: string;
    secret: string;
    provider: WebhookProvider;
    events: {
      order_created: boolean;
      order_updated: boolean;
      order_cancelled: boolean;
      order_delivered: boolean;
    };
  };
  shopify_config: {
    store_url: string;
    api_key: string;
    api_secret: string;
    webhook_secret: string;
    order_status_mapping: {
      [key: string]: string;
    };
  };
  prestashop_config: {
    store_url: string;
    api_key: string;
    webhook_secret: string;
    order_status_mapping: {
      [key: string]: string;
    };
  };
  quick_action_text: string;
  prompt_message: string;
  error_message: string;
  buttonVisible: boolean;
}

// Ajout d'une fonction pour obtenir le style du bouton actif
function getActiveBgStyle(isActive: boolean) {
  return isActive 
    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 border-transparent' 
    : 'bg-white/5 border-white/10 hover:bg-white/10';
}

export default function OrderTrackingSettings() {
  const { chatbotPublicId } = useChatbot();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'advanced'>('general');
  const [settings, setSettings] = useState<OrderTrackingSettings>({
    enabled: false,
    integration_type: null,
    tracking_url: '',
    webhook_config: {
      url: '',
      secret: '',
      provider: 'custom',
      events: {
        order_created: true,
        order_updated: true,
        order_cancelled: true,
        order_delivered: true
      }
    },
    shopify_config: {
      store_url: '',
      api_key: '',
      api_secret: '',
      webhook_secret: '',
      order_status_mapping: {
        'pending': 'Pending',
        'paid': 'Paid',
        'processing': 'Processing',
        'shipped': 'Shipped',
        'delivered': 'Delivered',
        'cancelled': 'Cancelled'
      }
    },
    prestashop_config: {
      store_url: '',
      api_key: '',
      webhook_secret: '',
      order_status_mapping: {
        '1': 'Awaiting Check Payment',
        '2': 'Payment Accepted',
        '3': 'Processing in Progress',
        '4': 'Shipped',
        '5': 'Delivered',
        '6': 'Canceled'
      }
    },
    quick_action_text: 'Track my order',
    prompt_message: 'Please enter your order number to track your order:',
    error_message: 'Sorry, we couldn\'t find that order. Please check the order number and try again.',
    buttonVisible: true
  });
  const [openGuide, setOpenGuide] = useState<'shopify' | 'prestashop' | 'webhook' | null>(null);

  useEffect(() => {
    if (chatbotPublicId) {
      loadSettings();
    }
  }, [chatbotPublicId]);

  const loadSettings = async () => {
    if (!chatbotPublicId) return;
    
    try {
      // Utiliser l'API centralisée de configuration du chatbot
      const configData = await getChatbotConfigSection<OrderTrackingSettings>(
        'order_tracking',
        chatbotPublicId
      );
      
      if (configData) {
        setSettings(configData);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatbotPublicId) return;
    
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      const success = await updateChatbotConfigSection(
        'order_tracking',
        settings,
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to save settings');
      }
      
      setSuccess(true);
      toast.success('Settings saved successfully!');
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save settings');
      toast.error('Failed to save settings. Please try again.');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleWebhookEventToggle = (event: keyof OrderTrackingSettings['webhook_config']['events']) => {
    setSettings(prev => ({
      ...prev,
      webhook_config: {
        ...prev.webhook_config,
        events: {
          ...prev.webhook_config.events,
          [event]: !prev.webhook_config.events[event]
        }
      }
    }));
  };

  const testWebhook = async () => {
    if (!settings.webhook_config.url) {
      setError('Please enter a webhook URL first');
      return;
    }

    setTestingWebhook(true);
    setError(null);

    try {
      const response = await fetch(settings.webhook_config.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Webhook-Secret': settings.webhook_config.secret
        },
        body: JSON.stringify({
          event: 'test',
          timestamp: new Date().toISOString(),
          data: {
            order_id: 'test-order-123',
            status: 'test',
            customer_email: 'test@example.com'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Webhook test failed');
      }

      setSuccess(true);
      toast.success('Webhook test successful!');
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to test webhook. Please check your URL and secret.');
      toast.error('Webhook test failed. Please check your URL and credentials.');
      console.error('Error testing webhook:', err);
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleIntegrationTypeChange = (type: IntegrationType) => {
    setSettings(prev => {
      // Mise à jour de l'état avec le nouveau type d'intégration
      const updatedSettings = {
      ...prev,
      integration_type: type
      };

      // Initialiser shopify_config si nécessaire
      if (type === 'shopify' && !prev.shopify_config) {
        updatedSettings.shopify_config = {
          store_url: '',
          api_key: '',
          api_secret: '',
          webhook_secret: '',
          order_status_mapping: {
            'pending': 'Pending',
            'paid': 'Paid',
            'processing': 'Processing',
            'shipped': 'Shipped',
            'delivered': 'Delivered',
            'cancelled': 'Cancelled'
          }
        };
      }

      // Initialiser prestashop_config si nécessaire
      if (type === 'prestashop' && !prev.prestashop_config) {
        updatedSettings.prestashop_config = {
          store_url: '',
          api_key: '',
          webhook_secret: '',
          order_status_mapping: {
            '1': 'Awaiting Check Payment',
            '2': 'Payment Accepted',
            '3': 'Processing in Progress',
            '4': 'Shipped',
            '5': 'Delivered',
            '6': 'Canceled'
          }
        };
      }

      // Initialiser webhook_config si nécessaire
      if (type === 'webhook' && !prev.webhook_config) {
        updatedSettings.webhook_config = {
          url: '',
          secret: '',
          provider: 'custom',
          events: {
            order_created: true,
            order_updated: true,
            order_cancelled: true,
            order_delivered: true
          }
        };
      }

      return updatedSettings;
    });
  };

  const handleShopifyConfigChange = (
    key: keyof OrderTrackingSettings['shopify_config'], 
    value: string | Record<string, string>
  ) => {
    setSettings(prev => ({
      ...prev,
      shopify_config: {
        ...prev.shopify_config,
        [key]: value
      }
    }));
  };

  const handlePrestaShopConfigChange = (
    key: keyof OrderTrackingSettings['prestashop_config'], 
    value: string | Record<string, string>
  ) => {
    setSettings(prev => ({
      ...prev,
      prestashop_config: {
        ...prev.prestashop_config,
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-full">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-lg bg-gradient-to-r from-violet-500/20 to-blue-400/20 text-indigo-400">
          <ShoppingBag className="w-6 h-6" />
        </div>
      <div>
          <h2 className="text-2xl font-bold text-white">Order Tracking</h2>
          <p className="text-gray-400">
            Configure how your chatbot handles order tracking and notifications
          </p>
        </div>
      </div>

      <div className="flex space-x-2 border-b border-white/10 mb-6">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'general'
              ? 'text-white border-b-2 border-indigo-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          General Settings
        </button>
        <button
          onClick={() => setActiveTab('advanced')}
          className={`px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'advanced'
              ? 'text-white border-b-2 border-indigo-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Advanced Configuration
        </button>
        </div>
        
      <form onSubmit={handleSubmit} className="w-full">
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 w-full">
            <div className="bg-black/30 rounded-xl p-6 shadow-lg border border-white/5">
              <h3 className="text-lg font-medium text-white mb-6">General Settings</h3>
        
        <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-white/[0.03] rounded-lg border border-white/5">
            <div>
                    <h4 className="text-sm font-medium text-white">Enable Order Tracking</h4>
                    <p className="text-xs text-gray-400 mt-1">
                      Allow your customers to track their orders through the chatbot
              </p>
            </div>
            <Switch 
              checked={settings.enabled} 
                    onChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                    className={`${settings.enabled ? 'bg-gradient-to-r from-violet-500 to-blue-400' : 'bg-gray-700'} relative inline-flex h-6 w-11 items-center rounded-full`}
                    disabled={saving}
                  >
                    <span className="sr-only">Enable Order Tracking</span>
                    <span
                      className={`${settings.enabled ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                    />
                  </Switch>
          </div>
          
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-white">Integration Type</h4>
                  <p className="text-xs text-gray-400 mb-3">
                    Choose which integration to use for order tracking
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        settings.integration_type === 'shopify' 
                          ? 'border-indigo-500 bg-gradient-to-br from-violet-500/40 to-blue-400/40' 
                          : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                      onClick={() => handleIntegrationTypeChange('shopify')}
                    >
                      <div className="flex flex-col items-center text-center h-full">
                        <div className="w-12 h-12 bg-gradient-to-br from-violet-500/20 to-blue-400/20 rounded-full flex items-center justify-center mb-3">
                          <ShoppingBag className="w-6 h-6 text-indigo-400" />
                        </div>
                        <span className="text-sm font-medium text-white">Shopify</span>
                        <p className="text-xs text-gray-400 mt-2">Connect to your Shopify store</p>
                      </div>
                    </div>
                    
                    <div
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        settings.integration_type === 'prestashop' 
                          ? 'border-indigo-500 bg-gradient-to-br from-violet-500/40 to-blue-400/40' 
                          : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                      onClick={() => handleIntegrationTypeChange('prestashop')}
                    >
                      <div className="flex flex-col items-center text-center h-full">
                        <div className="w-12 h-12 bg-gradient-to-br from-violet-500/20 to-blue-400/20 rounded-full flex items-center justify-center mb-3">
                          <Store className="w-6 h-6 text-indigo-400" />
                    </div>
                        <span className="text-sm font-medium text-white">PrestaShop</span>
                        <p className="text-xs text-gray-400 mt-2">Connect to your PrestaShop store</p>
                      </div>
                    </div>
                    
                    <div
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        settings.integration_type === 'webhook' 
                          ? 'border-indigo-500 bg-gradient-to-br from-violet-500/40 to-blue-400/40' 
                          : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                      onClick={() => handleIntegrationTypeChange('webhook')}
                    >
                      <div className="flex flex-col items-center text-center h-full">
                        <div className="w-12 h-12 bg-gradient-to-br from-violet-500/20 to-blue-400/20 rounded-full flex items-center justify-center mb-3">
                          <Webhook className="w-6 h-6 text-indigo-400" />
                        </div>
                        <span className="text-sm font-medium text-white">Custom Webhook</span>
                        <p className="text-xs text-gray-400 mt-2">Use custom webhooks for integration</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
                  
            {settings.integration_type && (
              <div className="space-y-6">
                <div className="bg-black/30 rounded-xl p-6 shadow-lg border border-white/5">
                  <h3 className="text-lg font-medium text-white capitalize mb-6">
                    {settings.integration_type === 'webhook' ? 'Custom Webhook' : `${settings.integration_type} Integration`}
                  </h3>
                  
                  {/* Setup Guide Dropdown */}
                  <div className="mb-8">
                    <button
                      type="button"
                      onClick={() => setOpenGuide(openGuide === settings.integration_type ? null : settings.integration_type)}
                      className="flex items-center justify-between w-full p-4 bg-indigo-900/20 border border-indigo-500/30 rounded-lg text-white hover:bg-indigo-900/30 transition-colors"
                    >
                      <div className="flex items-center">
                        <Info className="w-5 h-5 text-indigo-400 mr-2" />
                        <span className="font-medium">Setup Guide</span>
                      </div>
                      {openGuide === settings.integration_type ? (
                        <ChevronUp className="w-5 h-5 text-indigo-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-indigo-400" />
                      )}
                    </button>
                    
                    {openGuide === 'shopify' && (
                      <div className="mt-4 p-5 bg-black/40 rounded-lg border border-white/10 text-sm text-gray-300 space-y-4">
                        <h4 className="font-medium text-white">Shopify Integration Guide</h4>
                        <p>Follow these steps to connect your Shopify store:</p>
                        
                        <ol className="space-y-4 ml-5 list-decimal">
                          <li>
                            <div className="font-medium text-white">Create a private app in your Shopify admin</div>
                            <p>Go to Apps &gt; App and sales channel settings &gt; Develop apps &gt; Create an app</p>
                          </li>
                          <li>
                            <div className="font-medium text-white">Configure app permissions</div>
                            <p>Add the following API access scopes:</p>
                            <ul className="list-disc ml-5 mt-2 space-y-1 text-gray-400">
                              <li>read_orders</li>
                              <li>read_customers</li>
                              <li>read_products</li>
                            </ul>
                          </li>
                          <li>
                            <div className="font-medium text-white">Copy API credentials</div>
                            <p>From the API credentials tab, copy the API key and API secret password</p>
                          </li>
                          <li>
                            <div className="font-medium text-white">Set up webhook (optional)</div>
                            <p>Add a webhook subscription for order events (created, updated, etc.) with the following URL:</p>
                            <div className="bg-gray-800 p-3 rounded mt-2 font-mono text-xs">
                              https://your-domain.com/api/webhooks/shopify
                            </div>
                            <p className="mt-2">Note the webhook secret provided by Shopify - you'll need to enter it below.</p>
                          </li>
                        </ol>
                        
                        <div className="bg-indigo-900/30 p-4 rounded-lg border border-indigo-500/30 flex items-start">
                          <Info className="w-5 h-5 text-indigo-400 mr-2 mt-0.5 flex-shrink-0" />
                          <p>Your customers will be able to track orders by entering their order number or email address associated with the order.</p>
                        </div>
                      </div>
                    )}
                    
                    {openGuide === 'prestashop' && (
                      <div className="mt-4 p-5 bg-black/40 rounded-lg border border-white/10 text-sm text-gray-300 space-y-4">
                        <h4 className="font-medium text-white">PrestaShop Integration Guide</h4>
                        <p>Follow these steps to connect your PrestaShop store:</p>
                        
                        <ol className="space-y-4 ml-5 list-decimal">
                          <li>
                            <div className="font-medium text-white">Create a WebService key in PrestaShop</div>
                            <p>Go to Advanced Parameters &gt; Webservice &gt; Add new webservice key</p>
                          </li>
                          <li>
                            <div className="font-medium text-white">Generate a key with appropriate permissions</div>
                            <p>Enable the following resources with GET access:</p>
                            <ul className="list-disc ml-5 mt-2 space-y-1 text-gray-400">
                              <li>orders</li>
                              <li>customers</li>
                              <li>order_details</li>
                              <li>order_states</li>
                            </ul>
                          </li>
                          <li>
                            <div className="font-medium text-white">Copy the API key</div>
                            <p>Save the generated API key - you'll need to enter it below</p>
                          </li>
                          <li>
                            <div className="font-medium text-white">Configure order status mapping</div>
                            <p>In the Advanced Configuration tab, you can map PrestaShop order status IDs to friendly customer-facing messages</p>
                          </li>
                        </ol>
                        
                        <div className="bg-indigo-900/30 p-4 rounded-lg border border-indigo-500/30 flex items-start">
                          <Info className="w-5 h-5 text-indigo-400 mr-2 mt-0.5 flex-shrink-0" />
                          <p>Make sure your PrestaShop store has WebService API enabled in the WebService tab under Advanced Parameters.</p>
                        </div>
                      </div>
                    )}
                    
                    {openGuide === 'webhook' && (
                      <div className="mt-4 p-5 bg-black/40 rounded-lg border border-white/10 text-sm text-gray-300 space-y-4">
                        <h4 className="font-medium text-white">Custom Webhook Integration Guide</h4>
                        <p>Use custom webhooks to integrate with any order management system:</p>
                        
                        <ol className="space-y-4 ml-5 list-decimal">
                          <li>
                            <div className="font-medium text-white">Configure your order system to send webhook events</div>
                            <p>Set up your e-commerce platform, ERP, or order management system to send webhook notifications for order events</p>
                          </li>
                          <li>
                            <div className="font-medium text-white">Create a webhook endpoint</div>
                            <p>Build a webhook receiver at a URL you control that can receive and process order events</p>
                          </li>
                          <li>
                            <div className="font-medium text-white">Set a webhook secret</div>
                            <p>Create a secure random string to use as your webhook secret, and configure your sender to include it in requests</p>
                          </li>
                          <li>
                            <div className="font-medium text-white">Send events in the required format</div>
                            <p>Your webhook should send data in the following JSON format:</p>
                            <div className="bg-gray-800 p-3 rounded mt-2 font-mono text-xs overflow-auto">
                              {`{
  "event": "order_created", // or other supported event type
  "timestamp": "2023-06-15T14:22:31Z",
  "data": {
    "order_id": "ORD-12345",
    "customer_email": "customer@example.com",
    "status": "processing",
    "items": [...],  // optional order items
    "tracking_number": "TRK123456789" // optional tracking info
  }
}`}
                            </div>
                          </li>
                        </ol>
                        
                        <div className="bg-indigo-900/30 p-4 rounded-lg border border-indigo-500/30 flex items-start">
                          <Info className="w-5 h-5 text-indigo-400 mr-2 mt-0.5 flex-shrink-0" />
                          <p>The webhook secret should be included in the X-Webhook-Secret header with each request for authentication.</p>
                        </div>
                        
                        <div className="mt-3">
                          <button 
                            type="button" 
                            className="inline-flex items-center text-sm text-indigo-400 hover:text-indigo-300"
                            onClick={() => window.open('/docs/webhook-api', '_blank')}
                          >
                            <Code className="w-4 h-4 mr-1" />
                            View full API documentation
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </button>
                        </div>
                    </div>
                    )}
                  </div>
                  
                  <div className="space-y-6">
                    {settings.integration_type === 'shopify' && (
                    <>
                        <div>
                          <label htmlFor="shopify_store_url" className="block text-sm font-medium text-gray-300 mb-2">
                            Store URL
                          </label>
                          <input
                            id="shopify_store_url"
                            type="url"
                            value={settings.shopify_config?.store_url || ''}
                            onChange={(e) => {
                              if (!settings.shopify_config) {
                                setSettings(prev => ({
                                  ...prev,
                                  shopify_config: {
                                    store_url: e.target.value,
                                    api_key: '',
                                    api_secret: '',
                                    webhook_secret: '',
                                    order_status_mapping: {
                                      'pending': 'Pending',
                                      'paid': 'Paid',
                                      'processing': 'Processing',
                                      'shipped': 'Shipped',
                                      'delivered': 'Delivered',
                                      'cancelled': 'Cancelled'
                                    }
                                  }
                                }));
                              } else {
                                handleShopifyConfigChange('store_url', e.target.value);
                              }
                            }}
                            placeholder="https://your-store.myshopify.com"
                            className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            disabled={saving}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="shopify_api_key" className="block text-sm font-medium text-gray-300 mb-2">
                              API Key
                            </label>
                            <input
                              id="shopify_api_key"
                              type="text"
                              value={settings.shopify_config?.api_key || ''}
                              onChange={(e) => {
                                if (!settings.shopify_config) {
                                  setSettings(prev => ({
                                    ...prev,
                                    shopify_config: {
                                      store_url: '',
                                      api_key: e.target.value,
                                      api_secret: '',
                                      webhook_secret: '',
                                      order_status_mapping: {
                                        'pending': 'Pending',
                                        'paid': 'Paid',
                                        'processing': 'Processing',
                                        'shipped': 'Shipped',
                                        'delivered': 'Delivered',
                                        'cancelled': 'Cancelled'
                                      }
                                    }
                                  }));
                                } else {
                                  handleShopifyConfigChange('api_key', e.target.value);
                                }
                              }}
                              placeholder="Your Shopify API Key"
                              className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              disabled={saving}
                            />
                          </div>
                          <div>
                            <label htmlFor="shopify_api_secret" className="block text-sm font-medium text-gray-300 mb-2">
                              API Secret
                            </label>
                            <input
                              id="shopify_api_secret"
                              type="password"
                              value={settings.shopify_config?.api_secret || ''}
                              onChange={(e) => {
                                if (!settings.shopify_config) {
                                  setSettings(prev => ({
                                    ...prev,
                                    shopify_config: {
                                      store_url: '',
                                      api_key: '',
                                      api_secret: e.target.value,
                                      webhook_secret: '',
                                      order_status_mapping: {
                                        'pending': 'Pending',
                                        'paid': 'Paid',
                                        'processing': 'Processing',
                                        'shipped': 'Shipped',
                                        'delivered': 'Delivered',
                                        'cancelled': 'Cancelled'
                                      }
                                    }
                                  }));
                                } else {
                                  handleShopifyConfigChange('api_secret', e.target.value);
                                }
                              }}
                              placeholder="Your Shopify API Secret"
                              className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              disabled={saving}
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor="shopify_webhook_secret" className="block text-sm font-medium text-gray-300 mb-2">
                            Webhook Secret
                          </label>
                          <input
                            id="shopify_webhook_secret"
                            type="password"
                            value={settings.shopify_config?.webhook_secret || ''}
                            onChange={(e) => {
                              if (!settings.shopify_config) {
                                setSettings(prev => ({
                                  ...prev,
                                  shopify_config: {
                                    store_url: '',
                                    api_key: '',
                                    api_secret: '',
                                    webhook_secret: e.target.value,
                                    order_status_mapping: {
                                      'pending': 'Pending',
                                      'paid': 'Paid',
                                      'processing': 'Processing',
                                      'shipped': 'Shipped',
                                      'delivered': 'Delivered',
                                      'cancelled': 'Cancelled'
                                    }
                                  }
                                }));
                              } else {
                                handleShopifyConfigChange('webhook_secret', e.target.value);
                              }
                            }}
                            placeholder="Your Shopify Webhook Secret"
                            className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            disabled={saving}
                          />
                        </div>
                      </>
                    )}
                      
                    {settings.integration_type === 'prestashop' && (
                      <>
                        <div>
                          <label htmlFor="prestashop_store_url" className="block text-sm font-medium text-gray-300 mb-2">
                            Store URL
                          </label>
                          <input
                            id="prestashop_store_url"
                            type="url"
                            value={settings.prestashop_config?.store_url || ''}
                            onChange={(e) => {
                              if (!settings.prestashop_config) {
                                setSettings(prev => ({
                                  ...prev,
                                  prestashop_config: {
                                    store_url: e.target.value,
                                    api_key: '',
                                    webhook_secret: '',
                                    order_status_mapping: {
                                      '1': 'Awaiting Check Payment',
                                      '2': 'Payment Accepted',
                                      '3': 'Processing in Progress',
                                      '4': 'Shipped',
                                      '5': 'Delivered',
                                      '6': 'Canceled'
                                    }
                                  }
                                }));
                              } else {
                                handlePrestaShopConfigChange('store_url', e.target.value);
                              }
                            }}
                            placeholder="https://your-store.com"
                            className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            disabled={saving}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="prestashop_api_key" className="block text-sm font-medium text-gray-300 mb-2">
                              API Key
                            </label>
                            <input
                              id="prestashop_api_key"
                              type="text"
                              value={settings.prestashop_config?.api_key || ''}
                              onChange={(e) => {
                                if (!settings.prestashop_config) {
                                  setSettings(prev => ({
                                    ...prev,
                                    prestashop_config: {
                                      store_url: '',
                                      api_key: e.target.value,
                                      webhook_secret: '',
                                      order_status_mapping: {
                                        '1': 'Awaiting Check Payment',
                                        '2': 'Payment Accepted',
                                        '3': 'Processing in Progress',
                                        '4': 'Shipped',
                                        '5': 'Delivered',
                                        '6': 'Canceled'
                                      }
                                    }
                                  }));
                                } else {
                                  handlePrestaShopConfigChange('api_key', e.target.value);
                                }
                              }}
                              placeholder="Your PrestaShop API Key"
                              className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              disabled={saving}
                            />
                          </div>
                          <div>
                            <label htmlFor="prestashop_webhook_secret" className="block text-sm font-medium text-gray-300 mb-2">
                              Webhook Secret
                            </label>
                            <input
                              id="prestashop_webhook_secret"
                              type="password"
                              value={settings.prestashop_config?.webhook_secret || ''}
                              onChange={(e) => {
                                if (!settings.prestashop_config) {
                                  setSettings(prev => ({
                                    ...prev,
                                    prestashop_config: {
                                      store_url: '',
                                      api_key: '',
                                      webhook_secret: e.target.value,
                                      order_status_mapping: {
                                        '1': 'Awaiting Check Payment',
                                        '2': 'Payment Accepted',
                                        '3': 'Processing in Progress',
                                        '4': 'Shipped',
                                        '5': 'Delivered',
                                        '6': 'Canceled'
                                      }
                                    }
                                  }));
                                } else {
                                  handlePrestaShopConfigChange('webhook_secret', e.target.value);
                                }
                              }}
                              placeholder="Your PrestaShop Webhook Secret"
                              className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              disabled={saving}
                            />
                          </div>
                        </div>
                      </>
                    )}
                        
                    {settings.integration_type === 'webhook' && (
                      <>
                        <div>
                          <label htmlFor="webhook_url" className="block text-sm font-medium text-gray-300 mb-2">
                            Webhook URL
                          </label>
                          <input
                            id="webhook_url"
                            type="url"
                            value={settings.webhook_config.url}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              webhook_config: { ...prev.webhook_config, url: e.target.value }
                            }))}
                            placeholder={settings.webhook_config.provider === 'zapier' 
                              ? "https://hooks.zapier.com/hooks/catch/..." 
                              : settings.webhook_config.provider === 'make'
                              ? "https://hook.eu1.make.com/..."
                              : "https://your-domain.com/webhook"}
                            className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            disabled={saving}
                          />
                        </div>
                        
                        <div className="mt-5">
                          <label className="block text-sm font-medium text-gray-300 mb-3">
                            Webhook Provider
                          </label>
                          <div className="grid grid-cols-3 gap-4">
                            <button
                              type="button"
                              onClick={() => setSettings(prev => ({
                                ...prev,
                                webhook_config: { ...prev.webhook_config, provider: 'zapier' }
                              }))}
                              className={`flex items-center justify-center gap-2 p-3 rounded-lg border ${
                                settings.webhook_config.provider === 'zapier'
                                  ? 'border-indigo-500/50 bg-gradient-to-r from-violet-500 to-blue-400'
                                  : 'border-white/10 bg-white/5 hover:bg-white/10'
                              } transition-colors`}
                            >
                              <Zap className={`w-5 h-5 ${settings.webhook_config.provider === 'zapier' ? 'text-white' : 'text-gray-400'}`} />
                              <span className={`${settings.webhook_config.provider === 'zapier' ? 'text-white' : 'text-gray-300'}`}>Zapier</span>
                            </button>
                            
                            <button
                              type="button"
                              onClick={() => setSettings(prev => ({
                                ...prev,
                                webhook_config: { ...prev.webhook_config, provider: 'make' }
                              }))}
                              className={`flex items-center justify-center gap-2 p-3 rounded-lg border ${
                                settings.webhook_config.provider === 'make'
                                  ? 'border-indigo-500/50 bg-gradient-to-r from-violet-500 to-blue-400'
                                  : 'border-white/10 bg-white/5 hover:bg-white/10'
                              } transition-colors`}
                            >
                              <RefreshCw className={`w-5 h-5 ${settings.webhook_config.provider === 'make' ? 'text-white' : 'text-gray-400'}`} />
                              <span className={`${settings.webhook_config.provider === 'make' ? 'text-white' : 'text-gray-300'}`}>Make</span>
                            </button>
                            
                            <button
                              type="button"
                              onClick={() => setSettings(prev => ({
                                ...prev,
                                webhook_config: { ...prev.webhook_config, provider: 'custom' }
                              }))}
                              className={`flex items-center justify-center gap-2 p-3 rounded-lg border ${
                                settings.webhook_config.provider === 'custom'
                                  ? 'border-indigo-500/50 bg-gradient-to-r from-violet-500 to-blue-400'
                                  : 'border-white/10 bg-white/5 hover:bg-white/10'
                              } transition-colors`}
                            >
                              <Webhook className={`w-5 h-5 ${settings.webhook_config.provider === 'custom' ? 'text-white' : 'text-gray-400'}`} />
                              <span className={`${settings.webhook_config.provider === 'custom' ? 'text-white' : 'text-gray-300'}`}>Custom</span>
                            </button>
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="webhook_secret" className="block text-sm font-medium text-gray-300 mb-2">
                            Webhook Secret
                          </label>
                          <div className="relative">
                            <input
                              id="webhook_secret"
                              type="password"
                              value={settings.webhook_config.secret}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                webhook_config: { ...prev.webhook_config, secret: e.target.value }
                              }))}
                              placeholder="Enter your webhook secret"
                              className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              disabled={saving}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-3">
                            Events to Receive
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                            {Object.entries(settings.webhook_config.events).map(([event, enabled]) => (
                              <label key={event} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 border border-white/5">
                                <input
                                  type="checkbox"
                                  checked={enabled}
                                  onChange={() => handleWebhookEventToggle(event as keyof OrderTrackingSettings['webhook_config']['events'])}
                                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  disabled={saving}
                                />
                                <span className="text-sm text-gray-300 capitalize">
                                  {event.replace(/_/g, ' ')}
                                </span>
                              </label>
                            ))}
                          </div>
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={testWebhook}
                              disabled={testingWebhook || saving || !settings.webhook_config.url}
                              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-violet-500 to-blue-400 hover:from-violet-600 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all"
                            >
                              {testingWebhook ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Testing Webhook...
                                </>
                              ) : (
                                <>
                                  <Zap className="w-4 h-4 mr-2" />
                                  Test Webhook
                                </>
                              )}
                            </button>
                      </div>
                      
                          {/* Provider-specific documentation */}
                          {settings.webhook_config.provider === 'zapier' && (
                            <div className="mt-6 p-4 bg-orange-900/10 border border-orange-500/20 rounded-lg">
                              <div className="flex items-center gap-2 mb-3">
                                <Zap className="w-5 h-5 text-orange-400" />
                                <h4 className="text-sm font-medium text-white">Zapier Integration Guide</h4>
                              </div>
                              <p className="text-sm text-gray-300 mb-3">
                                Connect your chatbot to thousands of apps with Zapier:
                              </p>
                              <ol className="space-y-2 text-sm text-gray-300 ml-5 list-decimal">
                                <li>Create a new Zap in Zapier</li>
                                <li>Choose "Webhook by Zapier" as the trigger app</li>
                                <li>Select "Catch Hook" as the trigger event</li>
                                <li>Copy the webhook URL provided by Zapier</li>
                                <li>Paste it in the Webhook URL field above</li>
                                <li>Save your settings and test the webhook</li>
                              </ol>
                              <div className="mt-3">
                                <a 
                                  href="https://zapier.com/apps/webhook/integrations" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-sm text-orange-400 hover:text-orange-300"
                                >
                                  Learn more about Zapier Webhooks
                                  <ExternalLink className="w-3 h-3 ml-1" />
                                </a>
                              </div>
                            </div>
                          )}
                          
                          {settings.webhook_config.provider === 'make' && (
                            <div className="mt-6 p-4 bg-blue-900/10 border border-blue-500/20 rounded-lg">
                              <div className="flex items-center gap-2 mb-3">
                                <RefreshCw className="w-5 h-5 text-blue-400" />
                                <h4 className="text-sm font-medium text-white">Make Integration Guide</h4>
                              </div>
                              <p className="text-sm text-gray-300 mb-3">
                                Connect your chatbot to Make (formerly Integromat):
                              </p>
                              <ol className="space-y-2 text-sm text-gray-300 ml-5 list-decimal">
                                <li>Create a new scenario in Make</li>
                                <li>Add a "Webhooks" module as the trigger</li>
                                <li>Select "Custom webhook" as the webhook type</li>
                                <li>Copy the webhook URL provided by Make</li>
                                <li>Paste it in the Webhook URL field above</li>
                                <li>Save your settings and test the webhook</li>
                              </ol>
                              <div className="mt-3">
                                <a 
                                  href="https://www.make.com/en/help/tools/webhooks" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-sm text-blue-400 hover:text-blue-300"
                                >
                                  Learn more about Make Webhooks
                                  <ExternalLink className="w-3 h-3 ml-1" />
                                </a>
                              </div>
                            </div>
                          )}
                      </div>
                    </>
                    )}
                  </div>
                </div>
              </div>
                  )}
                </div>
              )}
              
        {activeTab === 'advanced' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            <div className="bg-black/30 rounded-xl p-6 shadow-lg border border-white/5">
              <h3 className="text-lg font-medium text-white mb-6">User Interface Settings</h3>
              
              <div className="space-y-6">
              <div>
                  <label htmlFor="quick_action_text" className="block text-sm font-medium text-gray-300 mb-2">
                  Quick Action Text
                </label>
                <input
                    id="quick_action_text"
                  type="text"
                  value={settings.quick_action_text}
                  onChange={(e) => setSettings(prev => ({ ...prev, quick_action_text: e.target.value }))}
                  placeholder="Track my order"
                    className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    disabled={saving}
                />
                  <p className="mt-2 text-xs text-gray-400">
                  This text will appear as a quick action button in the chat
                </p>
              </div>
              
              <div>
                  <label htmlFor="prompt_message" className="block text-sm font-medium text-gray-300 mb-2">
                  Prompt Message
                </label>
                  <input
                    id="prompt_message"
                    type="text"
                  value={settings.prompt_message}
                  onChange={(e) => setSettings(prev => ({ ...prev, prompt_message: e.target.value }))}
                  placeholder="Please enter your order number to track your order:"
                    className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    disabled={saving}
                />
                  <p className="mt-2 text-xs text-gray-400">
                    This message will be shown when asking for the order number
                </p>
              </div>
              
              <div>
                  <label htmlFor="error_message" className="block text-sm font-medium text-gray-300 mb-2">
                    Error Message
                  </label>
                  <textarea
                    id="error_message"
                    value={settings.error_message}
                    onChange={(e) => setSettings(prev => ({ ...prev, error_message: e.target.value }))}
                    placeholder="Sorry, we couldn't find that order. Please check the order number and try again."
                    className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    rows={2}
                    disabled={saving}
                  />
                  <p className="mt-2 text-xs text-gray-400">
                    This message will be shown when order tracking fails or the order is not found
                  </p>
              </div>
              
              <div>
                  <label htmlFor="tracking_url" className="block text-sm font-medium text-gray-300 mb-2">
                    Tracking URL Format
                </label>
                  <input
                    id="tracking_url"
                    type="text"
                    value={settings.tracking_url}
                    onChange={(e) => setSettings(prev => ({ ...prev, tracking_url: e.target.value }))}
                    placeholder="https://your-domain.com/tracking/{order_id}"
                    className="block w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    disabled={saving}
                  />
                  <p className="mt-2 text-xs text-gray-400">
                    Use {'{order_id}'} as a placeholder for the order ID
                  </p>
                </div>
              </div>
            </div>

            {/* Order Status Mapping - show this section always in Advanced tab */}
            <div className="bg-black/30 rounded-xl p-6 shadow-lg border border-white/5">
              <h3 className="text-lg font-medium text-white mb-6">Order Status Mapping</h3>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-400 mb-4">
                  Map your store's order statuses to friendly customer-facing messages
                </p>
                
                {settings.integration_type === 'shopify' && (
                  <div className="space-y-4">
                    {settings.shopify_config && Object.entries(settings.shopify_config.order_status_mapping).map(([status, label]) => (
                      <div key={status} className="grid grid-cols-2 gap-4">
                        <div className="bg-white/5 rounded-lg px-4 py-3 flex items-center border border-white/10">
                          <span className="text-sm text-gray-300 capitalize">{status}</span>
                        </div>
                        <input
                          type="text"
                          value={label}
                          onChange={(e) => {
                            const newMapping = { ...settings.shopify_config.order_status_mapping };
                            newMapping[status] = e.target.value;
                            handleShopifyConfigChange('order_status_mapping', newMapping);
                          }}
                          className="rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          disabled={saving}
                        />
                      </div>
                    ))}
                  </div>
                )}
                
                {settings.integration_type === 'prestashop' && (
                  <div className="space-y-4">
                    {settings.prestashop_config && Object.entries(settings.prestashop_config.order_status_mapping).map(([status, label]) => (
                      <div key={status} className="grid grid-cols-2 gap-4">
                        <div className="bg-white/5 rounded-lg px-4 py-3 flex items-center border border-white/10">
                          <span className="text-sm text-gray-300">Status {status}</span>
                        </div>
                        <input
                          type="text"
                          value={label}
                          onChange={(e) => {
                            const newMapping = { ...settings.prestashop_config.order_status_mapping };
                            newMapping[status] = e.target.value;
                            handlePrestaShopConfigChange('order_status_mapping', newMapping);
                          }}
                          className="rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          disabled={saving}
                        />
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Toujours afficher un mappage générique par défaut quand l'intégration est webhook ou non définie */}
                {(settings.integration_type === 'webhook' || settings.integration_type === null) && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg px-4 py-3 flex items-center border border-white/10">
                        <span className="text-sm text-gray-300 capitalize">pending</span>
                      </div>
                      <input
                        type="text"
                        defaultValue="Your order has been received and is being processed"
                        placeholder="Enter a friendly message for pending status"
                        className="rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        onChange={(e) => {
                          if (!settings.shopify_config) {
                            setSettings(prev => ({
                              ...prev,
                              shopify_config: {
                                store_url: '',
                                api_key: '',
                                api_secret: '',
                                webhook_secret: '',
                                order_status_mapping: {
                                  'pending': e.target.value,
                                  'paid': 'Paid',
                                  'processing': 'Processing',
                                  'shipped': 'Shipped',
                                  'delivered': 'Delivered',
                                  'cancelled': 'Cancelled'
                                }
                              }
                            }));
                          } else {
                          const newMapping = { ...settings.shopify_config.order_status_mapping };
                          newMapping['pending'] = e.target.value;
                          handleShopifyConfigChange('order_status_mapping', newMapping);
                          }
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg px-4 py-3 flex items-center border border-white/10">
                        <span className="text-sm text-gray-300 capitalize">processing</span>
                      </div>
                      <input
                        type="text"
                        defaultValue="Your order is being prepared for shipping"
                        placeholder="Enter a friendly message for processing status"
                        className="rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        onChange={(e) => {
                          if (!settings.shopify_config) {
                            setSettings(prev => ({
                              ...prev,
                              shopify_config: {
                                store_url: '',
                                api_key: '',
                                api_secret: '',
                                webhook_secret: '',
                                order_status_mapping: {
                                  'pending': 'Pending',
                                  'paid': 'Paid',
                                  'processing': e.target.value,
                                  'shipped': 'Shipped',
                                  'delivered': 'Delivered',
                                  'cancelled': 'Cancelled'
                                }
                              }
                            }));
                          } else {
                          const newMapping = { ...settings.shopify_config.order_status_mapping };
                          newMapping['processing'] = e.target.value;
                          handleShopifyConfigChange('order_status_mapping', newMapping);
                          }
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg px-4 py-3 flex items-center border border-white/10">
                        <span className="text-sm text-gray-300 capitalize">shipped</span>
                      </div>
                      <input
                        type="text"
                        defaultValue="Your order is on its way to you!"
                        placeholder="Enter a friendly message for shipped status"
                        className="rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        onChange={(e) => {
                          if (!settings.shopify_config) {
                            setSettings(prev => ({
                              ...prev,
                              shopify_config: {
                                store_url: '',
                                api_key: '',
                                api_secret: '',
                                webhook_secret: '',
                                order_status_mapping: {
                                  'pending': 'Pending',
                                  'paid': 'Paid',
                                  'processing': 'Processing',
                                  'shipped': e.target.value,
                                  'delivered': 'Delivered',
                                  'cancelled': 'Cancelled'
                                }
                              }
                            }));
                          } else {
                          const newMapping = { ...settings.shopify_config.order_status_mapping };
                          newMapping['shipped'] = e.target.value;
                          handleShopifyConfigChange('order_status_mapping', newMapping);
                          }
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg px-4 py-3 flex items-center border border-white/10">
                        <span className="text-sm text-gray-300 capitalize">delivered</span>
                      </div>
                      <input
                        type="text"
                        defaultValue="Your order has been delivered. Enjoy!"
                        placeholder="Enter a friendly message for delivered status"
                        className="rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        onChange={(e) => {
                          if (!settings.shopify_config) {
                            setSettings(prev => ({
                              ...prev,
                              shopify_config: {
                                store_url: '',
                                api_key: '',
                                api_secret: '',
                                webhook_secret: '',
                                order_status_mapping: {
                                  'pending': 'Pending',
                                  'paid': 'Paid',
                                  'processing': 'Processing',
                                  'shipped': 'Shipped',
                                  'delivered': e.target.value,
                                  'cancelled': 'Cancelled'
                                }
                              }
                            }));
                          } else {
                          const newMapping = { ...settings.shopify_config.order_status_mapping };
                          newMapping['delivered'] = e.target.value;
                          handleShopifyConfigChange('order_status_mapping', newMapping);
                          }
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg px-4 py-3 flex items-center border border-white/10">
                        <span className="text-sm text-gray-300 capitalize">cancelled</span>
                      </div>
                      <input
                        type="text"
                        defaultValue="Your order has been cancelled"
                        placeholder="Enter a friendly message for cancelled status"
                        className="rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        onChange={(e) => {
                          if (!settings.shopify_config) {
                            setSettings(prev => ({
                              ...prev,
                              shopify_config: {
                                store_url: '',
                                api_key: '',
                                api_secret: '',
                                webhook_secret: '',
                                order_status_mapping: {
                                  'pending': 'Pending',
                                  'paid': 'Paid',
                                  'processing': 'Processing',
                                  'shipped': 'Shipped',
                                  'delivered': 'Delivered',
                                  'cancelled': e.target.value
                                }
                              }
                            }));
                          } else {
                          const newMapping = { ...settings.shopify_config.order_status_mapping };
                          newMapping['cancelled'] = e.target.value;
                          handleShopifyConfigChange('order_status_mapping', newMapping);
                          }
                        }}
                      />
                    </div>
                    {settings.integration_type === null && (
                      <div className="pt-4 pb-2">
                        <p className="text-amber-400/80 text-sm flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Select an integration type first to save these mappings
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
          
        <div className="flex justify-end mt-8">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-violet-500 to-blue-400 hover:from-violet-600 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              // Force la mise à jour de la configuration pour activer le bouton
              if (!chatbotPublicId) return;
              
              const forcedSettings = {
                ...settings,
                enabled: true,
                buttonVisible: true
              };
              
              setSaving(true);
              setError(null);
              
              updateChatbotConfigSection(
                'order_tracking',
                forcedSettings,
                chatbotPublicId
              ).then(() => {
                toast.success('Button visibility forces to VISIBLE');
                setSettings(forcedSettings);
                setTimeout(() => {
                  window.location.reload();
                }, 1500);
              }).catch(err => {
                setError('Failed to force button visibility');
                console.error('Error forcing button visibility:', err);
              }).finally(() => {
                setSaving(false);
              });
            }}
            className="ml-4 inline-flex items-center px-6 py-3 border border-indigo-300 text-sm font-medium rounded-lg text-white bg-transparent hover:bg-indigo-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all"
          >
            Force Button Visibility
          </button>
        </div>
      </form>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 text-red-400 rounded-lg border border-red-500/20">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-500/10 text-green-400 rounded-lg border border-green-500/20 animate-fade-in">
          <CheckCircle2 className="w-5 h-5" />
          <p>Settings saved successfully!</p>
        </div>
      )}
    </div>
  );
}